import dayjs from 'dayjs';
import { roundTo } from './helpers';

/**
 * Convert amount from one currency to another
 * @param {number} amount - Amount to convert
 * @param {string} fromCurrency - Source currency
 * @param {string} toCurrency - Target currency
 * @param {Object} exchangeRates - Exchange rates object with baseCurrency and rates
 * @returns {number} Converted amount
 */
export const convertCurrency = (amount, fromCurrency, toCurrency, exchangeRates) => {
  if (fromCurrency === toCurrency) return amount;

  // Default rates to use when exchangeRates isn't loaded or a rate is missing
  // These match the defaults in DataContext and can be overridden by user in Settings
  const defaultRates = {
    GBP: 1.00,
    USD: 1.27,
    EUR: 1.17,
    JPY: 188.50,
  };

  const baseCurrency = exchangeRates?.baseCurrency || 'GBP';
  const rates = exchangeRates?.rates || defaultRates;

  // Helper to get rate, falling back to defaults if not in user-configured rates
  const getRate = (currency) => {
    if (rates[currency]) return rates[currency];
    if (defaultRates[currency]) {
      console.warn(`Currency conversion: Using default rate for ${currency} (configure in Settings)`);
      return defaultRates[currency];
    }
    // Last resort: 1:1 conversion for unknown currencies
    console.warn(`Currency conversion: No rate for ${currency}, using 1:1 (add rate in Settings)`);
    return 1;
  };

  // Convert to base currency first if needed
  let amountInBase = amount;
  if (fromCurrency !== baseCurrency) {
    const rate = getRate(fromCurrency);
    amountInBase = roundTo(amount / rate, 10);
  }

  // Convert from base to target currency
  if (toCurrency !== baseCurrency) {
    const rate = getRate(toCurrency);
    return roundTo(amountInBase * rate, 10);
  }

  return amountInBase;
};

/**
 * Calculate goal value from multiple portfolio allocations
 * @param {Object} goal - Goal with allocations array
 * @param {Array} portfolios - Array of portfolio objects
 * @param {Object} exchangeRates - Exchange rates object
 * @returns {number} Total goal value in goal's currency
 */
export const calculateMultiPortfolioGoalValue = (goal, portfolios, exchangeRates) => {
  if (!goal?.allocations || goal.allocations.length === 0) return 0;
  if (!portfolios || portfolios.length === 0) return 0;

  let totalValue = 0;

  goal.allocations.forEach(allocation => {
    const portfolio = portfolios?.find(p => p.id === allocation.portfolioId);

    // FIX: Add proper null checks
    if (!portfolio || portfolio.currentValue == null || !portfolio.currency) {
      console.warn(`Portfolio not found or invalid for allocation:`, allocation.portfolioId);
      return;
    }

    let allocatedValue;

    // Support both old (percentage) and new (type + value) allocation formats
    if (allocation.type === 'value' || allocation.type === 'amount') {
      // Fixed amount allocation
      allocatedValue = allocation.value ?? 0;
    } else if (!allocation.type || allocation.type === 'percentage') {
      // Percentage-based allocation (default if type not set)
      const percentValue = allocation.value !== undefined ? allocation.value : allocation.percentage;
      if (percentValue == null || percentValue < 0) {
        console.warn(`Invalid percentage value for allocation:`, percentValue);
        return;
      }
      allocatedValue = (portfolio.currentValue ?? 0) * (percentValue / 100);
    } else if (allocation.percentage !== undefined) {
      // Legacy format support
      allocatedValue = (portfolio.currentValue ?? 0) * (allocation.percentage / 100);
    } else {
      return;
    }

    // Convert to goal's currency
    const convertedValue = convertCurrency(
      allocatedValue,
      portfolio.currency,
      goal.currency || 'GBP',
      exchangeRates
    );

    totalValue += convertedValue;
  });

  return totalValue;
};

/**
 * Calculate the current value of a goal based on portfolio allocation
 * @param {number} portfolioValue - Current portfolio value
 * @param {number} allocationPercentage - Percentage allocated to goal
 * @returns {number} Current goal value
 */
export const calculateGoalValue = (portfolioValue, allocationPercentage) => {
  return portfolioValue * (allocationPercentage / 100);
};

/**
 * Calculate the gain/loss for a goal proportionally
 * @param {number} portfolioTotalGain - Total portfolio gain/loss
 * @param {number} portfolioValue - Current portfolio value
 * @param {number} goalValue - Current goal value
 * @returns {number} Goal gain/loss
 */
export const calculateGoalGain = (portfolioTotalGain, portfolioValue, goalValue) => {
  if (portfolioValue === 0) return 0;
  return (portfolioTotalGain / portfolioValue) * goalValue;
};

/**
 * Calculate monthly contribution for a goal based on portfolio allocations
 * @param {Object} goal - Goal object with allocations
 * @param {Array} portfolios - Array of portfolio objects
 * @param {Object} exchangeRates - Exchange rates object
 * @returns {number} Total monthly contribution in goal's currency
 */
export const calculateGoalMonthlyContribution = (goal, portfolios, exchangeRates) => {
  if (!goal.allocations || goal.allocations.length === 0) {
    // Fallback to legacy monthlyContribution field if no allocations
    return goal.monthlyContribution || 0;
  }

  let totalMonthly = 0;

  goal.allocations.forEach(allocation => {
    const portfolio = portfolios.find(p => p.id === allocation.portfolioId);
    if (!portfolio || !portfolio.monthlyContribution) return;

    const portfolioContribution = portfolio.monthlyContribution || 0;

    if (allocation.type === 'percentage' || !allocation.type) {
      // Percentage-based allocation
      const percentValue = allocation.value !== undefined ? allocation.value : allocation.percentage;
      if (percentValue === undefined) return;
      const allocatedContribution = (portfolioContribution * percentValue) / 100;

      // Convert to goal currency
      totalMonthly += convertCurrency(
        allocatedContribution,
        portfolio.currency,
        goal.currency,
        exchangeRates
      );
    } else if (allocation.type === 'value' || allocation.type === 'amount') {
      // Fixed-value allocation - distribute contribution proportionally
      if (portfolio.currentValue > 0) {
        const allocationRatio = allocation.value / portfolio.currentValue;
        const allocatedContribution = portfolioContribution * allocationRatio;

        // Convert to goal currency
        totalMonthly += convertCurrency(
          allocatedContribution,
          portfolio.currency,
          goal.currency,
          exchangeRates
        );
      }
    }
  });

  return totalMonthly;
};

/**
 * Calculate if a goal is on track
 * @param {Object} goal - Goal object
 * @param {number} currentValue - Current goal value
 * @param {Array} portfolios - Array of portfolio objects (optional, for contribution calculation)
 * @param {Object} exchangeRates - Exchange rates object (optional, for contribution calculation)
 * @returns {Object} { isOnTrack, requiredMonthly, monthsRemaining }
 */
export const calculateGoalStatus = (goal, currentValue, portfolios = [], exchangeRates = null) => {
  const targetDate = dayjs(goal.targetDate);
  const today = dayjs();

  const monthsRemaining = targetDate.diff(today, 'month', true);

  if (monthsRemaining <= 0) {
    return {
      isOnTrack: currentValue >= goal.targetAmount,
      requiredMonthly: 0,
      monthsRemaining: 0,
      status: currentValue >= goal.targetAmount ? 'achieved' : 'overdue'
    };
  }

  const amountNeeded = goal.targetAmount - currentValue;
  const requiredMonthly = amountNeeded > 0 ? amountNeeded / monthsRemaining : 0;

  // Calculate effective monthly contribution from portfolios
  const effectiveMonthlyContribution = portfolios.length > 0 && exchangeRates
    ? calculateGoalMonthlyContribution(goal, portfolios, exchangeRates)
    : (goal.monthlyContribution || 0); // Fallback to legacy field

  const isOnTrack = effectiveMonthlyContribution >= requiredMonthly;

  return {
    isOnTrack,
    requiredMonthly,
    monthsRemaining,
    effectiveMonthlyContribution,
    status: isOnTrack ? 'on-track' : 'behind'
  };
};

/**
 * Calculate portfolio total gain/loss
 * @param {number} currentValue - Current portfolio value
 * @param {number} costBasis - Total amount invested
 * @returns {number} Total gain/loss
 */
export const calculatePortfolioGain = (currentValue, costBasis) => {
  return currentValue - costBasis;
};

/**
 * Calculate portfolio return percentage
 * @param {number} currentValue - Current portfolio value
 * @param {number} costBasis - Total amount invested
 * @returns {number} Return percentage
 */
export const calculateReturnPercentage = (currentValue, costBasis) => {
  if (costBasis === 0) return 0;
  return ((currentValue - costBasis) / costBasis) * 100;
};

/**
 * Calculate total contributions from transactions
 * @param {Array} transactions - Array of transaction objects
 * @returns {number} Total contributions
 */
export const calculateTotalContributions = (transactions) => {
  return transactions
    .filter(t => t.type === 'contribution')
    .reduce((sum, t) => sum + t.amount, 0);
};

/**
 * Calculate total withdrawals from transactions
 * @param {Array} transactions - Array of transaction objects
 * @returns {number} Total withdrawals
 */
export const calculateTotalWithdrawals = (transactions) => {
  return transactions
    .filter(t => t.type === 'withdrawal')
    .reduce((sum, t) => sum + t.amount, 0);
};

/**
 * Calculate goal progress percentage
 * @param {number} currentValue - Current goal value
 * @param {number} targetAmount - Target amount
 * @returns {number} Progress percentage (0-100)
 */
export const calculateProgressPercentage = (currentValue, targetAmount) => {
  if (targetAmount === 0) return 0;
  return Math.min((currentValue / targetAmount) * 100, 100);
};

/**
 * Distribute portfolio value changes proportionally to goals
 * @param {Array} goals - Array of goal objects
 * @param {number} oldPortfolioValue - Previous portfolio value
 * @param {number} newPortfolioValue - New portfolio value
 * @returns {Array} Updated goals with new allocations
 */
export const distributePortfolioChange = (goals, oldPortfolioValue, newPortfolioValue) => {
  // Keep the same allocation percentages, values will adjust automatically
  return goals.map(goal => ({
    ...goal,
    // Allocation percentage stays the same
    // The value will be recalculated based on new portfolio value
  }));
};

/**
 * Calculate time-weighted return
 * @param {Array} snapshots - Array of portfolio snapshots
 * @returns {number} Time-weighted return percentage
 */
export const calculateTimeWeightedReturn = (snapshots) => {
  if (snapshots.length < 2) return 0;

  const sortedSnapshots = [...snapshots].sort((a, b) =>
    dayjs(a.date).valueOf() - dayjs(b.date).valueOf()
  );

  const firstValue = sortedSnapshots[0].value;
  const lastValue = sortedSnapshots[sortedSnapshots.length - 1].value;

  if (firstValue === 0) return 0;

  return ((lastValue - firstValue) / firstValue) * 100;
};

/**
 * Calculate projected future value with compound growth and contributions
 * @param {number} currentValue - Current value
 * @param {number} annualReturnRate - Annual return rate as percentage (e.g., 5 for 5%)
 * @param {number} monthlyContribution - Monthly contribution amount
 * @param {number} months - Number of months to project
 * @returns {number} Projected future value
 */
export const calculateProjectedValue = (currentValue, annualReturnRate, monthlyContribution, months) => {
  if (months <= 0) return currentValue;
  
  const monthlyRate = annualReturnRate / 100 / 12;
  let futureValue = currentValue;
  
  // Compound growth with monthly contributions
  for (let i = 0; i < months; i++) {
    // Apply monthly return
    futureValue = futureValue * (1 + monthlyRate);
    // Add monthly contribution at end of month
    futureValue += monthlyContribution;
  }
  
  return futureValue;
};

/**
 * Calculate when a goal will be reached based on projections
 * @param {number} currentValue - Current goal value
 * @param {number} targetAmount - Target amount
 * @param {number} annualReturnRate - Annual return rate as percentage
 * @param {number} monthlyContribution - Monthly contribution amount
 * @returns {number|null} Number of months until goal is reached, or null if unreachable
 */
export const calculateGoalReachDate = (currentValue, targetAmount, annualReturnRate, monthlyContribution) => {
  // FIX: Check if goal is already reached
  if (currentValue >= targetAmount) return 0;

  const monthlyRate = annualReturnRate / 100 / 12;

  // FIX: Calculate total monthly growth to prevent infinite loops
  // The goal is reachable only if the total monthly increase is positive
  const monthlyGrowth = (currentValue * monthlyRate) + monthlyContribution;

  // If we're not growing or shrinking, goal is unreachable
  if (monthlyGrowth <= 0) {
    return null;
  }

  let value = currentValue;
  let months = 0;
  const maxMonths = 600; // 50 years max

  while (value < targetAmount && months < maxMonths) {
    // Apply monthly return first
    value = value * (1 + monthlyRate);
    // Add monthly contribution at end of month (matches calculateProjectedValue)
    value += monthlyContribution;
    months++;
  }

  return value >= targetAmount ? months : null;
};

/**
 * Calculate projected value over time for a goal
 * @param {Object} goal - Goal object
 * @param {Array} portfolios - Array of portfolio objects
 * @param {Array} transactions - Array of transaction objects
 * @param {Object} exchangeRates - Exchange rates object
 * @param {number} monthsToProject - Number of months to project forward (default: auto-calculate to goal date or 60)
 * @returns {Array} Array of { date, value, contributions } objects
 */
export const calculateGoalProjection = (goal, portfolios, transactions, exchangeRates, monthsToProject = null) => {
  if (!goal.allocations || goal.allocations.length === 0) {
    return [];
  }

  const today = dayjs();
  const projection = [];
  
  // Calculate current goal value
  const currentGoalValue = calculateMultiPortfolioGoalValue(goal, portfolios, exchangeRates);
  
  // Get all transactions for this goal's allocated portfolios
  const goalPortfolioIds = goal.allocations.map(a => a.portfolioId);
  const goalTransactions = transactions.filter(t => 
    t.goalId === goal.id || (t.portfolioId && goalPortfolioIds.includes(t.portfolioId))
  );
  
  // Calculate total contributions to goal (from transactions)
  const totalContributions = goalTransactions
    .filter(t => t.type === 'contribution' || t.type === 'manual')
    .reduce((sum, t) => {
      const portfolio = portfolios.find(p => p.id === t.portfolioId);
      if (!portfolio) return sum;
      return sum + convertCurrency(t.amount, portfolio.currency, goal.currency, exchangeRates);
    }, 0);
  
  // Calculate weighted low, average, and high return rates for goal
  let totalAllocatedValue = 0;
  let weightedLowReturnSum = 0;
  let weightedAverageReturnSum = 0;
  let weightedHighReturnSum = 0;

  goal.allocations.forEach(allocation => {
    const portfolio = portfolios.find(p => p.id === allocation.portfolioId);
    if (!portfolio) return;

    let allocatedValue;
    if (allocation.type === 'value' || allocation.type === 'amount') {
      allocatedValue = allocation.value;
    } else if (!allocation.type || allocation.type === 'percentage') {
      // Percentage-based allocation (default if type not set)
      const percentValue = allocation.value !== undefined ? allocation.value : allocation.percentage;
      if (percentValue === undefined) return;
      allocatedValue = portfolio.currentValue * (percentValue / 100);
    } else {
      return;
    }

    const convertedValue = convertCurrency(
      allocatedValue,
      portfolio.currency,
      goal.currency,
      exchangeRates
    );

    totalAllocatedValue += convertedValue;

    // Support both new and legacy return rate fields
    const lowReturn = portfolio.lowReturn || 3;
    const averageReturn = portfolio.averageReturn || portfolio.estimatedReturnRate || 5;
    const highReturn = portfolio.highReturn || portfolio.optimisticReturn || (portfolio.estimatedReturnRate ? portfolio.estimatedReturnRate + 3 : 8);

    weightedLowReturnSum += convertedValue * lowReturn;
    weightedAverageReturnSum += convertedValue * averageReturn;
    weightedHighReturnSum += convertedValue * highReturn;
  });

  const lowReturnRate = totalAllocatedValue > 0 ? weightedLowReturnSum / totalAllocatedValue : 3;
  const averageReturnRate = totalAllocatedValue > 0 ? weightedAverageReturnSum / totalAllocatedValue : 5;
  const highReturnRate = totalAllocatedValue > 0 ? weightedHighReturnSum / totalAllocatedValue : 8;

  // Calculate monthly contribution from portfolio allocations
  const monthlyContribution = calculateGoalMonthlyContribution(goal, portfolios, exchangeRates);

  // Calculate when goal will be reached (using low return rate)
  const monthsToReachGoalLow = calculateGoalReachDate(
    currentGoalValue,
    goal.targetAmount,
    lowReturnRate,
    monthlyContribution
  );

  // Calculate when goal will be reached (using average return rate)
  const monthsToReachGoalAverage = calculateGoalReachDate(
    currentGoalValue,
    goal.targetAmount,
    averageReturnRate,
    monthlyContribution
  );

  // Calculate when goal will be reached (using high return rate)
  const monthsToReachGoalHigh = calculateGoalReachDate(
    currentGoalValue,
    goal.targetAmount,
    highReturnRate,
    monthlyContribution
  );

  // Determine how many months to project
  let projectionMonths = monthsToProject;
  if (projectionMonths === null) {
    // Auto-calculate: use goal reach date, target date, or default to 60 months
    if (monthsToReachGoalAverage !== null) {
      projectionMonths = Math.ceil(monthsToReachGoalAverage * 1.1); // Extend 10% past goal reach
    } else if (goal.targetDate) {
      const targetDate = dayjs(goal.targetDate);
      projectionMonths = Math.max(60, Math.ceil(targetDate.diff(today, 'month', true)));
    } else {
      projectionMonths = 60;
    }
  }

  // If we know when goal will be reached, ensure we project at least that far
  if (monthsToReachGoalAverage !== null && monthsToReachGoalAverage > projectionMonths) {
    projectionMonths = Math.ceil(monthsToReachGoalAverage * 1.1);
  }

  // Generate projection data points for all three scenarios
  const lowProjection = [];
  const averageProjection = [];
  const highProjection = [];

  for (let month = 0; month <= projectionMonths; month++) {
    const date = today.add(month, 'month');

    // Low scenario
    const projectedValueLow = calculateProjectedValue(
      currentGoalValue,
      lowReturnRate,
      monthlyContribution,
      month
    );

    // Average scenario
    const projectedValueAverage = calculateProjectedValue(
      currentGoalValue,
      averageReturnRate,
      monthlyContribution,
      month
    );

    // High scenario
    const projectedValueHigh = calculateProjectedValue(
      currentGoalValue,
      highReturnRate,
      monthlyContribution,
      month
    );

    const contributionsToDate = totalContributions + (monthlyContribution * month);

    const isGoalReachedLow = projectedValueLow >= goal.targetAmount;
    const isGoalReachedAverage = projectedValueAverage >= goal.targetAmount;
    const isGoalReachedHigh = projectedValueHigh >= goal.targetAmount;

    lowProjection.push({
      date: date.format('YYYY-MM-DD'),
      displayDate: date.format('MMM YYYY'),
      value: projectedValueLow,
      contributions: contributionsToDate,
      goalReached: isGoalReachedLow && month === monthsToReachGoalLow,
    });

    averageProjection.push({
      date: date.format('YYYY-MM-DD'),
      displayDate: date.format('MMM YYYY'),
      value: projectedValueAverage,
      contributions: contributionsToDate,
      goalReached: isGoalReachedAverage && month === monthsToReachGoalAverage,
    });

    highProjection.push({
      date: date.format('YYYY-MM-DD'),
      displayDate: date.format('MMM YYYY'),
      value: projectedValueHigh,
      contributions: contributionsToDate,
      goalReached: isGoalReachedHigh && month === monthsToReachGoalHigh,
    });
  }

  return {
    projection: averageProjection, // Keep for backward compatibility
    lowProjection,
    averageProjection,
    highProjection,
    optimisticProjection: highProjection, // Keep for backward compatibility
    monthsToReachGoal: monthsToReachGoalAverage, // Keep for backward compatibility
    monthsToReachGoalLow,
    monthsToReachGoalAverage,
    monthsToReachGoalHigh,
    monthsToReachGoalOptimistic: monthsToReachGoalHigh, // Keep for backward compatibility
    goalReachDate: monthsToReachGoalAverage !== null ? today.add(monthsToReachGoalAverage, 'month') : null,
    goalReachDateLow: monthsToReachGoalLow !== null ? today.add(monthsToReachGoalLow, 'month') : null,
    goalReachDateAverage: monthsToReachGoalAverage !== null ? today.add(monthsToReachGoalAverage, 'month') : null,
    goalReachDateHigh: monthsToReachGoalHigh !== null ? today.add(monthsToReachGoalHigh, 'month') : null,
    goalReachDateOptimistic: monthsToReachGoalHigh !== null ? today.add(monthsToReachGoalHigh, 'month') : null, // Keep for backward compatibility
  };
};

/**
 * Get total contributions for a goal from transactions
 * @param {string} goalId - Goal ID
 * @param {Array} transactions - Array of transaction objects
 * @param {Array} portfolios - Array of portfolio objects
 * @param {string} goalCurrency - Goal currency
 * @param {Object} exchangeRates - Exchange rates object
 * @returns {number} Total contributions in goal currency
 */
export const getGoalContributions = (goalId, transactions, portfolios, goalCurrency, exchangeRates) => {
  const goalTransactions = transactions.filter(t => 
    t.goalId === goalId && (t.type === 'contribution' || t.type === 'manual')
  );
  
  return goalTransactions.reduce((sum, t) => {
    const portfolio = portfolios.find(p => p.id === t.portfolioId);
    if (!portfolio) return sum;
    return sum + convertCurrency(t.amount, portfolio.currency, goalCurrency, exchangeRates);
  }, 0);
};

/**
 * Calculate goal allocation breakdown with returns
 * @param {Object} goal - Goal object
 * @param {Array} portfolios - Array of portfolio objects
 * @param {Object} exchangeRates - Exchange rates object
 * @returns {Array} Array of allocation breakdown objects
 */
export const calculateGoalAllocationBreakdown = (goal, portfolios, exchangeRates) => {
  if (!goal.allocations || goal.allocations.length === 0) {
    return [];
  }
  
  return goal.allocations.map(allocation => {
    const portfolio = portfolios.find(p => p.id === allocation.portfolioId);
    if (!portfolio) return null;

    let allocatedValue;
    const isPercentage = !allocation.type || allocation.type === 'percentage';

    if (allocation.type === 'value' || allocation.type === 'amount') {
      allocatedValue = allocation.value;
    } else if (isPercentage) {
      const percentValue = allocation.value !== undefined ? allocation.value : allocation.percentage;
      if (percentValue === undefined) return null;
      allocatedValue = portfolio.currentValue * (percentValue / 100);
    } else {
      return null;
    }

    const convertedValue = convertCurrency(
      allocatedValue,
      portfolio.currency,
      goal.currency,
      exchangeRates
    );

    const gain = calculatePortfolioGain(portfolio.currentValue, portfolio.costBasis);
    const percentValue = allocation.value !== undefined ? allocation.value : allocation.percentage || 0;
    // For percentage allocations: gain is proportional to percentage
    // For fixed value allocations: gain is proportional to the ratio of allocated value to portfolio value
    const allocatedGain = isPercentage
      ? gain * (percentValue / 100)
      : (portfolio.currentValue > 0 && allocatedValue > 0)
        ? gain * (allocatedValue / portfolio.currentValue)
        : 0;
    
    const convertedGain = convertCurrency(
      allocatedGain,
      portfolio.currency,
      goal.currency,
      exchangeRates
    );
    
    return {
      portfolioId: portfolio.id,
      portfolioName: portfolio.name,
      allocationType: allocation.type,
      allocationValue: allocation.value,
      allocatedValue: convertedValue,
      allocatedGain: convertedGain,
      returnRate: portfolio.averageReturn || portfolio.estimatedReturnRate || 5,
      currency: portfolio.currency,
    };
  }).filter(Boolean);
};
